import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Changeset } from 'ember-changeset';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import Button from 'ember-wiser/components/button';
import Form from 'ember-wiser/components/form';
import LinkTo from 'ember-wiser/components/link-to';
import NavBar from 'parog-web/components/nav-bar';
import ProductName from 'parog-web/components/product-name';
import type Medic from 'parog-web/models/medic';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import type TermsOfUseService from 'parog-web/services/terms-of-use';
import PageContainer from 'ui-kit/components/page-container';

export default class TermsOfUseComponent extends Component {
  @service declare session: SemeiaSessionService;
  @service declare termsOfUse: TermsOfUseService;

  payloadKey = 'acceptTermsOfUse';

  get alreadyAcceptedAnyTermsOfUse() {
    return this.#user.acceptedTermsOfUseVersion;
  }

  get changeset() {
    // Memoization :
    Object.defineProperty(this, 'changeset', {
      value: Changeset({}),
      writable: false
    });

    return this.changeset;
  }

  get isButtonDisabled() {
    return !this.#acceptsTermsOfUse || this.saveTask.isRunning;
  }

  get limitDate() {
    return this.termsOfUse.limitDate;
  }

  get shouldAcceptLatestTermsOfUseVersion() {
    return Boolean(this.#user) && !this.#user.isAcceptedTermsOfUse;
  }

  get #acceptsTermsOfUse() {
    return this.changeset.get(this.payloadKey);
  }

  get #user() {
    return this.session.medic || (this.session.patient as Medic | Patient);
  }

  saveTask = task(async () => {
    if (!this.#acceptsTermsOfUse) return;

    this.#user.set('acceptedTermsOfUseVersion', this.termsOfUse.currentVersion);
    await this.#user.save();

    this.termsOfUse.continueNavigationAfterAcceptingTermsOfUse();
  });

  <template>
    <NavBar @shouldHideProfileIcon={{this.shouldAcceptLatestTermsOfUseVersion}}>
      <ProductName />
    </NavBar>

    <PageContainer @background='transparent'>
      <:content>
        <h1 class='text-purple text-h2 mb-4 font-bold'>
          {{#if this.session.patient}}
            Gardez le lien avec votre équipe médicale
          {{else}}
            Bienvenue sur
            <ProductName />
          {{/if}}
        </h1>

        <img
          src='/assets/ui-images/illustration-cgu.svg'
          alt='Une main tenant un smartphone'
          width='657'
          height='325'
        />

        {{#if this.shouldAcceptLatestTermsOfUseVersion}}
          {{#if this.alreadyAcceptedAnyTermsOfUse}}
            <p data-test-cgu-evolved>
              Celles-ci ont évolué.
            </p>
            <p>
              Vous avez donc jusqu’au
              {{this.limitDate}}
              pour nous faire part de votre éventuel refus en nous écrivant :
              <ul>
                <li>
                  Par e-mail : support@semeia.io ;
                </li>
              </ul>
              Dans ce cas, cela entraînera la fermeture de votre compte
              <ProductName />.
            </p>
          {{/if}}
        {{/if}}

        <div class='ui divider hidden'></div>

        <p class='mb-2'>La sécurité de vos données est au coeur de nos
          préoccupations :</p>
        {{#if this.shouldAcceptLatestTermsOfUseVersion}}

          <Form @data={{this.changeset}} as |form|>
            <div class='sm:flex'>
              <form.Checkbox
                aria-label="J'accepte les conditions générales d'utilisations"
                @name={{this.payloadKey}}
                @label="J'accepte les"
              />
              &nbsp;
              <LinkTo
                @route='view-terms-of-use'
                class='underline'
                aria-label="lien vers les conditions générales d'utilisations"
              >
                Conditions Générales d'Utilisations</LinkTo>
            </div>
          </Form>
          <div class='flex justify-end'>
            <Button
              {{on 'click' (perform this.saveTask)}}
              @icon='ArrowRight'
              disabled={{this.isButtonDisabled}}
              @ariaLabel='Valider'
              class='mt-4'
            >Valider</Button>
          </div>
        {{/if}}
      </:content>
    </PageContainer>
  </template>
}
